import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './MyUnits.css';
import { LayoutGrid, List, Plus, Monitor, Users, X, Calendar as CalendarIcon, Clock } from 'lucide-react';
import Sidebar from '../../components/layout/Sidebar/Sidebar';
import apiClient from '../../services/api';

const MyUnits = () => {
    const entity = JSON.parse(localStorage.getItem('entity') || '{}');
    const isCarRental = entity.entityType === 'car';
    const navigate = useNavigate();
    const [units, setUnits] = useState([]);
    const [allBookings, setAllBookings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    
    // Filters & Sorting
    const [activeFilter, setActiveFilter] = useState('All');

    // Edit Modal State
    const [editingUnit, setEditingUnit] = useState(null);
    const [editForm, setEditForm] = useState({ id: '', type: '', guests: 2, status: '', numericId: null, price: 0, quantity: 1 });

    // Schedule Modal State
    const [viewingScheduleUnit, setViewingScheduleUnit] = useState(null);

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                // Fetch Units
                const resUnits = await apiClient.get('/users/me/units');
                if (resUnits.data && resUnits.data.data) {
                    const fetchedUnits = resUnits.data.data.map(u => ({
                        numericId: u.id,
                        id: u.label || u.title || `Unit ${u.id}`,
                        type: u.type || 'Unit',
                        guests: u.guests || 2,
                        price: u.price || 0,
                        quantity: u.quantity || 1,
                        bookings: 'Live',
                        nextCheckIn: 'N/A',
                        status: u.status ? u.status.toUpperCase() : 'AVAILABLE'
                    }));
                    setUnits(fetchedUnits);
                }

                // Fetch All Bookings for filtering into specific schedules
                const resBookings = await apiClient.get('/bookings');
                if (resBookings.data && resBookings.data.data) {
                    setAllBookings(resBookings.data.data);
                }
            } catch (err) {
                console.error("Failed to load initial data:", err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchInitialData();
    }, []);

    const openEditModal = (unit) => {
        setEditingUnit(unit);
        setEditForm({ ...unit, price: unit.price || 0, quantity: unit.quantity || 1 });
    };

    const closeEditModal = () => {
        setEditingUnit(null);
    };

    const handleEditSave = async (e) => {
        e.preventDefault();
        try {
            // Make REAL update call to backend
            await apiClient.put(`/users/me/units/${editForm.numericId}`, {
                guests: editForm.guests,
                status: editForm.status,
                id_label: editForm.id,
                price: editForm.price,
                type: editForm.type,
                quantity: editForm.quantity
            });
            
            // Optimistically update the UI after successful backend update
            setUnits(prev => prev.map(u => u.numericId === editingUnit.numericId ? { ...u, ...editForm } : u));
            closeEditModal();
            alert('Unit details successfully updated on the server!');
        } catch (error) {
            console.error('Update failed:', error);
            alert('Failed to update the unit on the server. Please try again.');
        }
    };

    const openScheduleModal = (unit) => {
        setViewingScheduleUnit(unit);
    };

    const closeScheduleModal = () => {
        setViewingScheduleUnit(null);
    };

    // Filter units based on Active Tab
    const filteredUnits = units.filter(unit => {
        if (activeFilter === 'All') return true;
        if (activeFilter === 'Available') return unit.status === 'AVAILABLE';
        if (activeFilter === 'Booked') return unit.status === 'BOOKED';
        if (activeFilter === 'Maintenance') return unit.status === 'MAINTENANCE';
        return true;
    });

    // Get specific bookings for viewing schedule
    const getUnitBookings = (unit) => {
        if (!unit) return [];
        return allBookings.filter(b => {
             const bookingLabel = b.title || b.entity_type;
             // Naive matching by name or returning ID
             return bookingLabel.includes(unit.id.replace('Room ', '')) || unit.id.includes(b.entity_id);
        });
    };

    const scheduleBookings = viewingScheduleUnit ? getUnitBookings(viewingScheduleUnit) : [];

    return (
        <div className="my-units-container">
            <Sidebar />

            <main className="main-content">
                {/* Page Title Row */}
                <div className="page-top-row">
                    <div className="page-title-block">
                        <h1>My Listings / Units</h1>
                        <p>Manage and monitor all your property units in one place.</p>
                    </div>

                    <div className="page-controls">
                        {/* Filter Tabs - Fully Functional */}
                        <div className="filter-tabs">
                            {['All', 'Available', 'Booked', 'Maintenance'].map(f => (
                                <button 
                                    key={f}
                                    className={`tab ${activeFilter === f ? 'active' : ''}`}
                                    onClick={() => setActiveFilter(f)}
                                >
                                    {f}
                                </button>
                            ))}
                        </div>

                        {/* View Toggle */}
                        <div className="view-toggle">
                            <button className="toggle-btn active"><LayoutGrid size={16} /></button>
                            <button className="toggle-btn" onClick={() => navigate('/availability')}><CalendarIcon size={16} /></button>
                        </div>

                        {/* Add Button Removed per Admin Request */}
                    </div>
                </div>

                {/* Units Grid */}
                <div className="units-grid">
                    {filteredUnits.length > 0 ? filteredUnits.map((unit) => (
                        <div key={unit.numericId || unit.id} className="unit-card">
                            {/* Card Header */}
                            <div className="card-header">
                                <h3>{unit.id}</h3>
                                <span className={`unit-status-pill unit-status-${unit.status.toLowerCase()}`}>
                                    {unit.status}
                                </span>
                            </div>

                            {/* Room Specs */}
                            <div className="unit-specs">
                                <div className="unit-info-row">
                                    <Monitor size={16} /> <span>{unit.type}</span>
                                </div>
                                <div className="unit-info-row">
                                    <Users size={16} /> <span>{unit.guests} {isCarRental ? 'Seats' : 'Max Guests'}</span>
                                </div>
                                <div className="unit-info-row">
                                    <Plus size={16} /> <span>Quantity: {unit.quantity}</span>
                                </div>
                                <div className="unit-info-row">
                                    <span>💲 {Number(unit.price).toFixed(2)}{isCarRental ? ' / day' : ' / night'}</span>
                                </div>
                            </div>

                            {/* Divider */}
                            <div className="card-divider" />

                            {/* Detail Rows */}
                            <div className="unit-details">
                                {unit.status === 'MAINTENANCE' ? (
                                    <>
                                        <div className="detail-row">
                                            <span className="detail-label">Scheduled Until</span>
                                            <span className="detail-value orange">{unit.scheduledUntil || 'Tomorrow'}</span>
                                        </div>
                                        <div className="detail-row">
                                            <span className="detail-label">Issue</span>
                                            <span className="detail-value bold">{unit.issue || 'Routine Check'}</span>
                                        </div>
                                    </>
                                ) : unit.status === 'AVAILABLE' && unit.lastCleaned ? (
                                    <>
                                        <div className="detail-row">
                                            <span className="detail-label">Upcoming Bookings</span>
                                            <span className="detail-value bold">{unit.bookings}</span>
                                        </div>
                                        <div className="detail-row">
                                            <span className="detail-label">Last Cleaned</span>
                                            <span className="detail-value muted">{unit.lastCleaned}</span>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="detail-row">
                                            <span className="detail-label">Upcoming Bookings</span>
                                            <span className="detail-value bold">{unit.bookings}</span>
                                        </div>
                                        <div className="detail-row">
                                            <span className="detail-label">Next Check-in</span>
                                            <span className={`detail-value ${unit.nextCheckIn === 'Today' ? 'primary' : 'primary'}`}>
                                                {unit.nextCheckIn}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Card Actions */}
                            <div className="card-actions">
                                <button className="view-schedule-btn" onClick={() => openScheduleModal(unit)}>
                                    View Schedule
                                </button>
                                <button className="edit-link" onClick={() => openEditModal(unit)}>
                                    Edit
                                </button>
                            </div>
                        </div>
                    )) : (
                        <div className="no-units-message">
                            <p>No listings found for this category.</p>
                        </div>
                    )}
                </div>

                {/* Edit Modal Overlay */}
                {editingUnit && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h2>Edit {editingUnit.id}</h2>
                                <button className="close-btn" onClick={closeEditModal}><X size={20} /></button>
                            </div>
                            <form onSubmit={handleEditSave} className="modal-form">
                                <div className="form-group">
                                    <label>{isCarRental ? 'Vehicle Name / Model' : 'Unit Name / Number'}</label>
                                    <input 
                                        type="text" 
                                        value={editForm.id} 
                                        onChange={e => setEditForm({...editForm, id: e.target.value})}
                                    />
                                    <small className="form-note">Edit the display name.</small>
                                </div>
                                <div className="form-group">
                                    <label>Type</label>
                                    <input 
                                        type="text"
                                        value={editForm.type} 
                                        onChange={e => setEditForm({...editForm, type: e.target.value})}
                                        placeholder="e.g. Standard, Deluxe"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Price</label>
                                    <input 
                                        type="number"
                                        value={editForm.price} 
                                        onChange={e => setEditForm({...editForm, price: parseFloat(e.target.value) || 0})}
                                    />
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>{isCarRental ? 'Seats' : 'Max Guests'} / Seats</label>
                                        <input 
                                            type="number" min="1" max="10"
                                            value={editForm.guests} 
                                            onChange={e => setEditForm({...editForm, guests: parseInt(e.target.value)})} 
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Quantity</label>
                                        <input 
                                            type="number" min="1"
                                            value={editForm.quantity} 
                                            onChange={e => setEditForm({...editForm, quantity: parseInt(e.target.value) || 1})} 
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Status</label>
                                        <select 
                                            value={editForm.status} 
                                            onChange={e => setEditForm({...editForm, status: e.target.value})}
                                        >
                                            <option value="AVAILABLE">Available</option>
                                            <option value="BOOKED">Booked</option>
                                            <option value="MAINTENANCE">Maintenance</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="modal-actions">
                                    <button type="button" className="btn-cancel" onClick={closeEditModal}>Cancel</button>
                                    <button type="submit" className="btn-save">Update Server</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Schedule Modal Overlay */}
                {viewingScheduleUnit && (
                    <div className="modal-overlay">
                        <div className="modal-content schedule-modal-content">
                            <div className="modal-header">
                                <h2>Schedule for {viewingScheduleUnit.id}</h2>
                                <button className="close-btn" onClick={closeScheduleModal}><X size={20} /></button>
                            </div>
                            
                            <div className="schedule-events-container">
                                {scheduleBookings.length > 0 ? (
                                    scheduleBookings.map((bk, i) => (
                                        <div key={i} className={`schedule-event event-${bk.status.toLowerCase()}`}>
                                            <div className="event-date-block">
                                                <CalendarIcon size={16} className="event-icon" />
                                                <span className="event-text">
                                                    {new Date(bk.check_in).toLocaleDateString()} - {new Date(bk.check_out).toLocaleDateString()}
                                                </span>
                                            </div>
                                            <div className="event-details-block">
                                                 <span className={`status-tag status-${bk.status.toLowerCase()}`}>{bk.status}</span>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="no-events">
                                        <Clock size={40} className="empty-icon" />
                                        <p>No upcoming schedule for this unit.</p>
                                        <p className="empty-subtext">This unit is fully available for bookings.</p>
                                    </div>
                                )}
                            </div>
                            
                            <div className="modal-actions center-actions">
                                <button type="button" className="btn-save w-full" onClick={closeScheduleModal}>Close Schedule</button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default MyUnits;
